import {
  IonButton,
  IonButtons,
  IonContent,
  IonImg,
  IonInput,
  IonLabel,
  IonText,
  isPlatform,
} from "@ionic/react";
import { DniReader } from "@pepper/capacitor-dni-reader";
import classNames from "classnames";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { idniecap, PACEHandler } from "idniecap";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import DniImage from "@/assets/imgs/dni-image.webp";
import ENVIRONMENT from "@/environments/environment";
import useModal from "@/hooks/useModal";
import {
  AnalyticsOrigination,
  AnalyticsStepNumber,
} from "@/interfaces/AnalyticsInterface/AnalyticsInterface";
import { ManagedModalRoutes } from "@/interfaces/ManagedModalInterface/ManagedModalInterface";
import {
  OcrAPIData,
  OcrAvailabilityConfigurationTypes,
  OcrFlowTypes,
  OcrNfcRequest,
  OcrStatus,
} from "@/interfaces/OcrInterface/OcrInterface";
import { saveDniData } from "@/redux/eDniModal/dni/dni";
import { getCountryList } from "@/redux/eDniModal/eDniModalAPI";
import { postOcr, postOcrNfc } from "@/redux/ocr/ocrAPI";
import { PostOrder } from "@/redux/orderManaged/orderManagedInterface";
import {
  getTimeResult,
  registerNFCError,
  registerNFCSuccess,
} from "@/services/analytics";
import InsightService from "@/services/insights";
import { RootState } from "@/store";
import { checkNie, formatName } from "@/utils/dniHelper";
import {
  buildDataOrderInvoice,
  buildPostOrder,
  getAddressDataFromDefaultValue2,
} from "@/utils/orderHelper";

import NFCAnimationModal from "../../NFCAnimationModal/NFCAnimationModal";
import styles from "./DniScan.module.scss";

interface DniScanManagedProps {
  nextStep: (
    arg0: ManagedModalRoutes,
    isDocument?: boolean,
    orderInvoiceData?: PostOrder,
    ocrResponse?: OcrNfcRequest,
  ) => void;
  handleLoading: (arg0: boolean) => void;
  isDocument?: boolean;
}

export const DniScanManaged: React.FC<DniScanManagedProps> = ({
  nextStep,
  handleLoading,
  isDocument,
}) => {
  const dispatch = useDispatch();

  dayjs.extend(customParseFormat);

  const [can, setCan] = useState<string>("");
  const [canError, setCanError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const ocrRequestData = useRef<OcrAPIData | null>(null);
  const [remainingNFCAttempts, setRemainingNFCAttempts] = useState<
    number | null
  >(null);
  const [scanError, setScanError] = useState("");
  const [initTime] = useState<dayjs.Dayjs>(dayjs());
  const initReadingTime = useRef<dayjs.Dayjs | null>(null);
  const orderData = useSelector(
    (state: RootState) => state.orderData.data?.data,
  );
  const invoiceData = useSelector(
    (state: RootState) => state.invoiceManaged.data?.data,
  );
  const processTypeData = useSelector(
    (state: RootState) => state.configSlice.versionControl?.eco_process_type_app,
  );
  const { presentModal: presentNFCAnimationModal } =
    useModal(NFCAnimationModal);
  const merchantInfo = useSelector(
    (state: RootState) => state.merchantData.data,
  );

  const initTestDataAndroid = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    const testData = {
      apellido1: "TEST",
      apellido2: "TEST",
      certificadoAutenticacion: {
        apellidos: "TEST TEST",
        apellidosRepresentante: "",
        email: "",
        estado: 1,
        fechaFinValidez: "21/05/2023",
        fechaInicioValidez: "21/05/2021",
        fechaNacimiento: "",
        nif: "",
        nifRepresentante: "",
        nombre: "MARIA TEST",
        nombreRepresentante: "",
        tipo: "AC DNIE 004",
      },
      certificadoCA: {
        apellidos: "DNIE",
        apellidosRepresentante: "",
        email: "",
        estado: 0,
        fechaFinValidez: "13/06/2029",
        fechaInicioValidez: "13/06/2014",
        fechaNacimiento: "",
        nif: "",
        nifRepresentante: "",
        nombre: "AC DNIE 004",
        nombreRepresentante: "",
        tipo: "AC RAIZ DNIE 2",
      },
      certificadoFirma: {
        apellidos: "TEST TEST",
        apellidosRepresentante: "",
        email: "",
        estado: 1,
        fechaFinValidez: "21/05/2023",
        fechaInicioValidez: "21/05/2021",
        fechaNacimiento: "",
        nif: "",
        nifRepresentante: "",
        nombre: "MARIA TEST",
        nombreRepresentante: "",
        tipo: "AC DNIE 004",
      },
      datosICAO: {
        DG1: "YV1fH1pJREVTUEJPVjE3MTAyMDUwNTMzNDE2Mlc8PDw8PDw5NTAxMzA0RjI2MDUyMTRFU1A8PDw8PDw8PDw8PDBMT1BFWjxCRU5JVE88PENMQVJBPEVVR0VOSUE8PDw=",
        DG2: "dYJIVX9hgkhQAgEBf2CCSEihDoEBAoIBAIcCAQGIAgAIXy6CSDNGQUMAMDEwAAAASDMAAQAASCUAAAAAAAAAAAAAAAAAAAAAAwEBMwF6AAAAAAAAAAAADGpQICANCocKAAAAFGZ0eXBq…",
        DG13: "bYGzMIGwDAVMT1BFWgwGQkVOSVRPDA1DTEFSQSBFVUdFTklBEwowNTMzNDE2Mi1XEwozMCAwMSAxOTk1EwNFU1ATCjIxIDA1IDIwMjYTCUJPVjE3MTAyMBMBRgwGTUFEUklEDAZNQURS…",
        SOD: "d4IInTCCCJkGCSqGSIb3DQEHAqCCCIowggiGAgEDMQ0wCwYJYIZIAWUDBAIBMIHdBgZngQgBAQGggdIEgc8wgcwCAQAwBwYFKw4DAhowgb0wGQIBAQQUwPW2pbmHi3mwoLGQiVv04h3g…",
      },
      direccion:
        "AVDA ALBERT EINSTEIN 2 PBJ - SAN FERNANDO DE HENARES - MADRID",
      emisor: "ESP",
      erroresVerificacion: [],
      fechaNacimiento: "09/06/1999",
      fechaValidez: "21/05/2026",
      firma:
        "iVBORw0KGgoAAAANSUhEUgAAAKsAAABuCAAAAAB9przCAAANBGlDQ1BrQ0dDb2xv\r↵clNwYWNlR2VuZXJpY0dyYXlHYW1tYTJfMgAAWIWlVwdck9cWv9/IAJKwp4ywkWVA…",
      imagen:
        "iVBORw0KGgoAAAANSUhEUgAAATMAAAF6CAIAAAD6bSitAAAAAXNSR0IArs4c6QAA\r↵AIRlWElmTU0AKgAAAAgABQESAAMAAAABAAEAAAEaAAUAAAABAAAASgEbAAUAAAAB…",
      integridadDocumento: true,
      municipioActual: "",
      municipioNacimiento: "MADRID",
      nacionalidad: "ESP",
      nif: "84882097E",
      nombre: "MARIA TEST",
      nombreCompleto: "TEST TEST MARIA TEST",
      nombreMadre: "AAAAAAA",
      nombrePadre: "AAAAAA",
      numSoporte: "BOV171020",
      pemCertificadoFirmaSOD:
        "-----BEGIN CERTIFICATE-----↵MIIFizCCA3OgAwIBAgIQTsXV+Zf8rXRgf92GJvOqOzANBgkqhkiG9w0BAQsFADB…",
      provinciaActual: "",
      provinciaNacimiento: "MADRID",
      sexo: "F",
      can: "041088",
    };
    listenerScanned(testData);
  };

  const initTestDataAdressErrorAndroid = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    const testData = {
      apellido1: "TEST",
      apellido2: "TEST",
      certificadoAutenticacion: {
        apellidos: "TEST TEST",
        apellidosRepresentante: "",
        email: "",
        estado: 1,
        fechaFinValidez: "21/05/2023",
        fechaInicioValidez: "21/05/2021",
        fechaNacimiento: "",
        nif: "",
        nifRepresentante: "",
        nombre: "MARIA TEST",
        nombreRepresentante: "",
        tipo: "AC DNIE 004",
      },
      certificadoCA: {
        apellidos: "DNIE",
        apellidosRepresentante: "",
        email: "",
        estado: 0,
        fechaFinValidez: "13/06/2029",
        fechaInicioValidez: "13/06/2014",
        fechaNacimiento: "",
        nif: "",
        nifRepresentante: "",
        nombre: "AC DNIE 004",
        nombreRepresentante: "",
        tipo: "AC RAIZ DNIE 2",
      },
      certificadoFirma: {
        apellidos: "TEST TEST",
        apellidosRepresentante: "",
        email: "",
        estado: 1,
        fechaFinValidez: "21/05/2023",
        fechaInicioValidez: "21/05/2021",
        fechaNacimiento: "",
        nif: "",
        nifRepresentante: "",
        nombre: "MARIA TEST",
        nombreRepresentante: "",
        tipo: "AC DNIE 004",
      },
      datosICAO: {
        DG1: "YV1fH1pJREVTUEJPVjE3MTAyMDUwNTMzNDE2Mlc8PDw8PDw5NTAxMzA0RjI2MDUyMTRFU1A8PDw8PDw8PDw8PDBMT1BFWjxCRU5JVE88PENMQVJBPEVVR0VOSUE8PDw=",
        DG2: "dYJIVX9hgkhQAgEBf2CCSEihDoEBAoIBAIcCAQGIAgAIXy6CSDNGQUMAMDEwAAAASDMAAQAASCUAAAAAAAAAAAAAAAAAAAAAAwEBMwF6AAAAAAAAAAAADGpQICANCocKAAAAFGZ0eXBq…",
        DG13: "bYGzMIGwDAVMT1BFWgwGQkVOSVRPDA1DTEFSQSBFVUdFTklBEwowNTMzNDE2Mi1XEwozMCAwMSAxOTk1EwNFU1ATCjIxIDA1IDIwMjYTCUJPVjE3MTAyMBMBRgwGTUFEUklEDAZNQURS…",
        SOD: "d4IInTCCCJkGCSqGSIb3DQEHAqCCCIowggiGAgEDMQ0wCwYJYIZIAWUDBAIBMIHdBgZngQgBAQGggdIEgc8wgcwCAQAwBwYFKw4DAhowgb0wGQIBAQQUwPW2pbmHi3mwoLGQiVv04h3g…",
      },
      direccion:
        "AVDA ALBERT EINSTEIN 2 PBJ - SAN FERNANDO DE HENARES - MADRID",
      emisor: "ESP",
      erroresVerificacion: [],
      fechaNacimiento: "09/06/1999",
      fechaValidez: "21/05/2026",
      firma:
        "iVBORw0KGgoAAAANSUhEUgAAAKsAAABuCAAAAAB9przCAAANBGlDQ1BrQ0dDb2xv\r↵clNwYWNlR2VuZXJpY0dyYXlHYW1tYTJfMgAAWIWlVwdck9cWv9/IAJKwp4ywkWVA…",
      imagen:
        "iVBORw0KGgoAAAANSUhEUgAAATMAAAF6CAIAAAD6bSitAAAAAXNSR0IArs4c6QAA\r↵AIRlWElmTU0AKgAAAAgABQESAAMAAAABAAEAAAEaAAUAAAABAAAASgEbAAUAAAAB…",
      integridadDocumento: true,
      municipioActual: "",
      municipioNacimiento: "MADRID",
      nacionalidad: "ES",
      nif: "84882097E",
      nombre: "MARIA TEST",
      nombreCompleto: "TEST TEST MARIA TEST",
      nombreMadre: "AAAAAAA",
      nombrePadre: "AAAAAA",
      numSoporte: "BOV171020",
      pemCertificadoFirmaSOD:
        "-----BEGIN CERTIFICATE-----↵MIIFizCCA3OgAwIBAgIQTsXV+Zf8rXRgf92GJvOqOzANBgkqhkiG9w0BAQsFADB…",
      provinciaActual: "",
      provinciaNacimiento: "MADRID",
      sexo: "F",
      can: "041088",
    };
    listenerScanned(testData);
  };
  const initTestDataNieNationality = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    const testData2 = {
      address: "UNKNOWN",
      birth_date: "1981-09-14",
      card_access_number: "724713",
      client_certificate_status: "UNKNOWN - UNKNOWN",
      document_id: "Z6031329K",
      expiration_date: "2029-02-11",
      face_photo: "iVBORw0KGgoAAAANSUhEUgAAATMAAAF6CAIAAAD6bSitAAAAA",
      gender: "F",
      last_name: "TEST TEST",
      name: "MARIA",
      nationality: "Canadá", // Asigna el valor de nacionalidad a nationality si es necesario
      nacionalidad: "Canadá",
      root_certificate_status: "UNKNOWN - UNKNOWN",
      support_number: "E26838955",
      type: "NIE",
      nombreMadre: "MARIA",
      nif: "Z6031329K",
      nombrePadre: "FRANCISCO",
    };
    listenerScanned(testData2);
  };
  const initTestDataNieErrorNationality = async () => {
    const ocrData = await postOcr({
      flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
      availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
    });
    ocrRequestData.current = ocrData.data;
    const testData2 = {
      address: "UNKNOWN",
      birth_date: "1981-09-14",
      card_access_number: "724713",
      client_certificate_status: "UNKNOWN - UNKNOWN",
      document_id: "Z6031329K",
      expiration_date: "2029-02-11",
      face_photo: "iVBORw0KGgoAAAANSUhEUgAAATMAAAF6CAIAAAD6bSitAAAAA",
      gender: "F",
      last_name: "TEST TEST",
      name: "MARIA",
      nationality: "", // Asigna el valor de nacionalidad a nationality si es necesario
      nacionalidad: "",
      root_certificate_status: "UNKNOWN - UNKNOWN",
      support_number: "E26838955",
      type: "NIE",
      nombreMadre: "MARIA",
      nif: "Z6031329K",
      nombrePadre: "FRANCISCO",
    };
    listenerScanned(testData2);
  };
  const getCountries = async () => {
    try {
      const cList = await getCountryList();
      return cList[0].keys;
    } catch (error) {
      return [];
    }
  };

  const insightsDniData = (datosDNIe: Record<string, any>) =>
    Object.keys(datosDNIe).map((key) => ({ [key]: datosDNIe[key] }));

  const listenerScanned = async (data: any) => {
    setLoading(false);
    handleLoading(true);

    if (ocrRequestData.current) {
      try {
        const countryL = await getCountries();
        let ocrData: OcrNfcRequest;
        if (isPlatform("android")) {
          InsightService.logTrace(
            "scanDNI Android managed",
            data && insightsDniData(data),
          );
          if (isPlatform("android")) {
            if (isPlatform("android") && merchantInfo?.merchant_id) {
              InsightService.logTrace(
                "data merchant",
                insightsDniData(merchantInfo),
              );
            }
          }
          const nationalityCode = countryL.find(
            (key) => key.description_code === data.nationality,
          );

          ocrData = {
            type: data.type,
            name: formatName(data.name),
            birth_date: data.birth_date,
            birth_place: data.birth_place,
            last_name: formatName(data.last_name),
            expiration_date: data.expiration_date,
            document_id: data.document_id,
            nationality: nationalityCode?.code || "",
            gender: data.gender,
            support_number: data.support_number,
            face_photo: data.face_photo,
            card_access_number: data.card_access_number,
            address: data.address ? data.address : "UNKNOWN",
            fathers_name: data.fathers_name,
            mothers_name: data.mothers_name,
            signature_photo: data.signature_photo,
            client_certificate_status: data.client_certificate_status,
            root_certificate_status: data.root_certificate_status,
          };
        } else {
          InsightService.logTrace(
            "scanDNI ios managed",
            data && insightsDniData(data),
          );
          if (merchantInfo?.merchant_id) {
            InsightService.logTrace(
              "data merchant",
              insightsDniData(merchantInfo),
            );
          }
          const { datosDNIe } = data;
          const nationalityCode = countryL.find(
            (key) => key.alt_code === datosDNIe.nacionalidad,
          );
          // Temporary for iOS

          ocrData = {
            type: datosDNIe ? (checkNie(datosDNIe?.nif) ? "NIE" : "DNI") : "",
            name: formatName(datosDNIe?.nombre) || "",
            birth_date:
              /* testDATE || */ dayjs(
                datosDNIe?.fechaNacimiento,
                "DD/MM/YYYY",
              ).format("YYYY-MM-DD") || "",
            birth_place: datosDNIe?.municipioNacimiento || "",
            last_name:
              formatName(`${datosDNIe?.apellido1} ${datosDNIe?.apellido2}`) ||
              "",
            expiration_date:
              dayjs(datosDNIe?.fechaValidez, "DD/MM/YYYY").format(
                "YYYY-MM-DD",
              ) || "",
            document_id: /* testDNI || */ datosDNIe?.nif || "",
            nationality: nationalityCode?.code || "",
            gender: datosDNIe?.sexo || "",
            support_number: datosDNIe?.numSoporte || "",
            face_photo: datosDNIe?.imagen || "",
            card_access_number: datosDNIe?.can || can || "",
            address: datosDNIe?.direccion
              ? `${datosDNIe?.direccion} - ${datosDNIe?.municipioActual} - ${datosDNIe?.provinciaActual}`
              : "UNKNOWN",
            fathers_name: datosDNIe?.nombrePadre || "",
            mothers_name: datosDNIe.nombreMadre || "",
            signature_photo: datosDNIe?.firma || "",
            client_certificate_status:
              datosDNIe?.certificadoAutenticacion?.estado === 0 ||
              datosDNIe?.certificadoAutenticacion?.estado === 2
                ? "GOOD - GOOD"
                : "UNKNOWN",
            root_certificate_status:
              datosDNIe?.certificadoAutenticacion?.estado === 0 ||
              datosDNIe.certificadoAutenticacion?.estado === 2
                ? "GOOD - GOOD"
                : "UNKNOWN",
          };
        }
        const successAnalytics = {
          time_elapsed: initTime ? getTimeResult(initTime) : 0,
          time_reading: initReadingTime.current
            ? getTimeResult(initReadingTime.current)
            : 0,
          document_type: ocrData?.type || "",
          step_number: AnalyticsStepNumber.Simulation,
        };

        registerNFCSuccess(
          { origin: AnalyticsOrigination.App },
          successAnalytics,
        );
        if (!ocrData.nationality && ocrData.type === "NIE") {
          dispatch(saveDniData(ocrData));
          nextStep(
            ManagedModalRoutes.ORDER_PERSONAL_DATA,
            isDocument,
            undefined,
            ocrData,
          );
          return;
        }
        const ocrResponse = await postOcrNfc(
          ocrRequestData.current.ocr_document_id,
          ocrData,
        );
        handleLoading(false);
        if (ocrResponse.data.status !== OcrStatus.VLD) {
          registerNFCError(
            { origin: AnalyticsOrigination.App },
            initReadingTime.current
              ? getTimeResult(initReadingTime.current)
              : 0,
          );
          setRemainingNFCAttempts(ocrResponse.data.nfc_attempts_remaining);
          hasScanError("Se ha producido un error");
        } else {
          ocrData.birth_country =
            ocrResponse.data.third_party_response.birth_country;
          dispatch(saveDniData(ocrData));
          if (ocrData.address === "UNKNOWN" || ocrData.address === "") {
            nextStep(
              ManagedModalRoutes.ORDER_PERSONAL_DATA,
              isDocument,
              undefined,
              ocrData,
            );
          } else {
            try {
              if (!isDocument) {
                if (!orderData) return;
              } else if (!invoiceData) {
                return;
              }

              handleLoading(true);
              if (invoiceData || orderData) {
                const dataOrderInvoice = buildDataOrderInvoice(
                  orderData!,
                  invoiceData!,
                  {
                    isDocument: isDocument || false,
                    processType:
                      isDocument &&
                      processTypeData,
                  },
                );
                if (!dataOrderInvoice) return;

                const nameValue = ocrData.name;
                const surnameValue = ocrData.last_name;
                const identity = ocrData.document_id;
                const addressData = await getAddressDataFromDefaultValue2(
                  ocrData.address || "",
                );
                const birthDate = ocrData.birth_date;
                const { nationality } = ocrData;
                const { gender } = ocrData;
                if (
                  !addressData ||
                  Object.keys(addressData).length === 0 ||
                  !addressData.address
                ) {
                  handleLoading(false);
                  nextStep(
                    ManagedModalRoutes.ORDER_PERSONAL_DATA,
                    isDocument,
                    undefined,
                    ocrData,
                  );
                  return;
                }
                ocrData = { ...ocrData };
                ocrData.ocr_id = ocrRequestData.current.ocr_document_id;
                const ocrId = ocrData.ocr_id;
                dispatch(saveDniData(ocrData));
                const postOrder = buildPostOrder({
                  dataOrderInvoice,
                  nameValue,
                  surnameValue,
                  identity,
                  addressData,
                  ocrId,
                  birthDate,
                  nationality,
                  gender,
                  addresShippingData: isDocument
                    ? invoiceData?.third_party_response.applicant
                        .billing_address
                    : orderData?.applicant.billing_address,
                });
                nextStep(
                  ManagedModalRoutes.SHIPPING_CLIENT_MANAGED,
                  isDocument,
                  postOrder,
                );
              }
            } catch (error) {
              console.error("Error:", error);
              handleLoading(false);
              nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
            }
          }
        }
      } catch (error) {
        handleLoading(false);
        console.log(error);
        nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
      }
    } else {
      handleLoading(false);
      nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
    }
  };

  const hasScanError = (errorText: string) => {
    setScanError(errorText);
    setTimeout(() => {
      setScanError("");
    }, 3000);
  };

  const listenerError = (errorT: string) => {
    setLoading(false);
    registerNFCError(
      { origin: AnalyticsOrigination.App },
      initReadingTime.current ? getTimeResult(initReadingTime.current) : 0,
    );
    InsightService.logTrace("errorScanDNI", [{ error: errorT }]);
    // Show error message
    if (errorT === "nfcTagIncorrectCAN") {
      setCanError("El CAN es incorrecto");
    }
    hasScanError("Se ha producido un error durante la lectura");
  };

  const disabledNfcScanButton = () => {
    if (remainingNFCAttempts !== null && remainingNFCAttempts === 0) {
      return true;
    }
    return !can || loading;
  };

  const handleContinue = async () => {
    initReadingTime.current = dayjs();
    try {
      if (isPlatform("android")) {
        const ocrData = await postOcr({
          flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
          availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
        });
        ocrRequestData.current = ocrData.data;

        setLoading(true);

        let data;
        if (remainingNFCAttempts !== null && remainingNFCAttempts > 0) {
          setRemainingNFCAttempts(remainingNFCAttempts - 1);

          data = await DniReader.scanDocument({ can });
        } else if (remainingNFCAttempts === null) {
          data = await DniReader.scanDocument({ can });
        }

        if (!data) {
          registerNFCError(
            { origin: AnalyticsOrigination.App },
            initReadingTime.current
              ? getTimeResult(initReadingTime.current)
              : 0,
          );
          InsightService.logTrace("errorScanDNI", [{ error: data.error }]);
          setLoading(false);
          hasScanError("Se ha producido un error durante la lectura");
        } else {
          InsightService.logTrace(
            "scanDNI",
            data.datosDNIe && insightsDniData(data.datosDNIe),
          );
          listenerScanned(data);
        }
      } else if (isPlatform("ios")) {
        const response = await idniecap.configure({
          apiKey: ENVIRONMENT.IDNIECAP_LICENSE,
        });
        if (response) {
          InsightService.logTrace(
            "responseIdnieCap",
            insightsDniData(response),
          );
        }
        // Temporary for iOS
        const ocrData = await postOcr({
          flow: OcrFlowTypes.ORIGINATION_WITHOUT_PROPOSAL,
          availability_configuration: OcrAvailabilityConfigurationTypes.NFC,
        });
        ocrRequestData.current = ocrData.data;
        setLoading(true);
        const data = await idniecap.readPassport({
          accessKey: can,
          paceKeyReference: PACEHandler.CAN_PACE_KEY_REFERENCE,
          tags: ["DG1", "DG2", "DG7", "DG11", "DG13"],
          esDNIe: true,
        });
        if (data.error) {
          registerNFCError(
            { origin: AnalyticsOrigination.App },
            initReadingTime.current
              ? getTimeResult(initReadingTime.current)
              : 0,
          );
          InsightService.logTrace("errorScanDNI", [{ error: data.error }]);
          setLoading(false);
          hasScanError("Se ha producido un error durante la lectura");
        } else {
          InsightService.logTrace(
            "scanDNI",
            data.datosDNIe && insightsDniData(data.datosDNIe),
          );
          listenerScanned(data);
        }
      }
    } catch (error: any) {
      if (isPlatform("android")) {
        listenerError(error.message);
      } else {
        InsightService.logTrace("ErrorScanDNI", [
          { error: JSON.stringify(error) },
        ]);
        registerNFCError(
          { origin: AnalyticsOrigination.App },
          initReadingTime.current ? getTimeResult(initReadingTime.current) : 0,
        );
        nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
        handleLoading(false);
      }
    }
  };

  const handleSkip = async () => {
    nextStep(ManagedModalRoutes.ORDER_PERSONAL_DATA, isDocument);
  };

  const changeCanInput = (e: any) => {
    const { value } = e.target;
    if (value) {
      if (/^\d{6}$/.test(value)) {
        setCan(value);
        setCanError("");
      } else {
        setCan("");
        setCanError("El CAN debe tener 6 números");
        e.preventDefault();
      }
    } else {
      setCanError("El CAN debe tener 6 números");
    }
  };
  const handleNFCAnimationModal = () => {
    presentNFCAnimationModal({
      onDismiss: (next: boolean) => {
        if (next) {
          handleContinue();
        }
      },
    });
  };

  // const [testDNI, settestDNI] = useState('');
  // const [testDATE, settestDATE] = useState('');

  return (
    <IonContent className={styles.modalDniScanContainer}>
      {scanError && <div className={styles.scanErrorMessage}>{scanError}</div>}
      {ENVIRONMENT.ENV === "development" && (
        <button onClick={initTestDataAndroid} type="button">
          test value dni
        </button>
      )}
      {ENVIRONMENT.ENV === "development" && (
        <div>
          <button onClick={initTestDataAdressErrorAndroid} type="button">
            Test value dni con error
          </button>
        </div>
      )}
      {ENVIRONMENT.ENV === "development" && (
        <div>
          <button onClick={initTestDataNieErrorNationality} type="button">
            {" "}
            Test value nie con error
          </button>
        </div>
      )}
      {ENVIRONMENT.ENV === "development" && (
        <button onClick={initTestDataNieNationality} type="button">
          {" "}
          Test value nie
        </button>
      )}

      <IonText className={styles.modalDniScanTitle}>
        Confirmar identidad cliente
      </IonText>
      <IonLabel className={styles.modalDniScanDescription}>
        Si el cliente está presente, puedes confirmar su identidad escaneando su
        DNI o NIE ahora. De esta manera, no le solicitaremos nunca el dni en
        pasos posteriores.
      </IonLabel>
      <IonLabel className={styles.modalDniScanDescription}>
        Sólo necesitamos el CAN del documento del cliente. Es el dato que
        aparece en la esquina inferior derecha como muestra la imagen:
      </IonLabel>
      <div className={styles.dniScanImgWrapper}>
        <IonImg src={DniImage} />
      </div>
      {/*       <IonInput
        label="TEST DNI"
        labelPlacement="stacked"
        onIonInput={(e: any) => settestDNI((e.target.value).toUpperCase())}
        className="dniScan-can-input"
      />
      <IonInput
        label="TEST Fecha de nacimiento YYYY-MM-DD"
        labelPlacement="stacked"
        onIonInput={(e: any) => settestDATE(e.target.value)}
        className="dniScan-can-input"
      /> */}
      <IonInput
        label="CAN"
        type="tel"
        maxlength={6}
        labelPlacement="stacked"
        className={classNames(
          styles.dniScanCanInput,
          canError && "ion-invalid ",
          canError === "" && "ion-valid",
        )}
        // errorText={canError}
        onIonInput={changeCanInput}
      />
      {canError && <span className={styles.validationError}>{canError}</span>}
      <IonButtons className={styles.dniScanNextBtnWrapper}>
        <IonButton
          disabled={disabledNfcScanButton()}
          className={classNames("nextButton", styles.dniScanNextButton)}
          onClick={handleNFCAnimationModal}
        >
          Continuar
        </IonButton>
        <IonButton className={styles.dniScanSkipButton} onClick={handleSkip}>
          Saltar y rellenar manualmente
        </IonButton>
      </IonButtons>
    </IonContent>
  );
};
